import axios from 'axios';
import type {
  DeleteDocumentResponse,
  DocumentCategory,
  DocumentConflictStrategy,
  DocumentResponse,
  DocumentsSummaryResponse,
  PayslipType,
} from '@timesheet/schemas';
import { getApiBaseUrl } from '../lib/api-base-url';
import { downloadBlob } from '../lib/download-blob';

const API_URL = getApiBaseUrl();

export type { DocumentCategory, DocumentConflictStrategy, DocumentResponse, DocumentsSummaryResponse, PayslipType };

export interface ListDocumentsParams {
  category: DocumentCategory;
  userId?: number;
  year?: number;
  month?: number;
  payslipType?: PayslipType;
  onlyMine?: boolean;
  search?: string;
}

export interface UploadDocumentPayload {
  file: File;
  category: DocumentCategory;
  title?: string;
  description?: string;
  targetUserId?: number;
  payslipType?: PayslipType;
  payslipYear?: number;
  payslipMonth?: number;
  notify?: boolean;
  notifyByEmail?: boolean;
}

export interface UpdateDocumentPayload {
  title?: string;
  description?: string | null;
}

/**
 * Error the page handles itself (409 duplicate period, 413 too large, 415 wrong
 * type, 429 throttled). Every other status is left to the global axios
 * interceptor, which toasts the backend message.
 */
export class DocumentApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly retryAfterSeconds?: number,
  ) {
    super(code);
    this.name = 'DocumentApiError';
  }
}

const HANDLED_UPLOAD_STATUSES = [409, 413, 415, 429];

const codeFor = (status: number, data: unknown): string => {
  if (status === 413) return 'FILE_TOO_LARGE';
  if (status === 429) return 'TOO_MANY_REQUESTS';
  const message = (data as { message?: unknown } | undefined)?.message;
  return typeof message === 'string' ? message : `HTTP_${status}`;
};

export const documentsService = {
  summary: async (): Promise<DocumentsSummaryResponse> => {
    const response = await axios.get(`${API_URL}/documents/summary`);
    return response.data;
  },

  list: async (params: ListDocumentsParams): Promise<DocumentResponse[]> => {
    const query: Record<string, string | number> = { category: params.category };
    if (params.userId) query.userId = params.userId;
    if (params.year) query.year = params.year;
    if (params.month) query.month = params.month;
    if (params.payslipType) query.payslipType = params.payslipType;
    if (params.onlyMine) query.onlyMine = '1';
    if (params.search?.trim()) query.search = params.search.trim();
    const response = await axios.get(`${API_URL}/documents`, { params: query });
    return response.data;
  },

  /** 404 (not visible / other tenant / deleted) resolves to null instead of a raw toast. */
  get: async (id: number): Promise<DocumentResponse | null> => {
    const response = await axios.get(`${API_URL}/documents/${id}`, {
      validateStatus: (status) => status < 400 || status === 404,
    });
    return response.status === 404 ? null : response.data;
  },

  download: async (doc: Pick<DocumentResponse, 'id' | 'originalFileName'>): Promise<void> => {
    await downloadBlob(`${API_URL}/documents/${doc.id}/download`, doc.originalFileName);
  },

  upload: async (payload: UploadDocumentPayload, onConflict?: DocumentConflictStrategy): Promise<DocumentResponse> => {
    const formData = new FormData();
    formData.append('file', payload.file);
    formData.append('category', payload.category);
    if (payload.title) formData.append('title', payload.title);
    if (payload.description) formData.append('description', payload.description);
    if (payload.targetUserId) formData.append('targetUserId', String(payload.targetUserId));
    if (payload.payslipType) formData.append('payslipType', payload.payslipType);
    if (payload.payslipYear) formData.append('payslipYear', String(payload.payslipYear));
    if (payload.payslipMonth) formData.append('payslipMonth', String(payload.payslipMonth));
    if (payload.notify !== undefined) formData.append('notify', String(payload.notify));
    if (payload.notifyByEmail !== undefined) formData.append('notifyByEmail', String(payload.notifyByEmail));

    const response = await axios.post(`${API_URL}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: onConflict ? { onConflict } : undefined,
      validateStatus: (status) => status < 400 || HANDLED_UPLOAD_STATUSES.includes(status),
    });

    if (response.status >= 400) {
      const retryAfter = Number(response.headers?.['retry-after']);
      throw new DocumentApiError(response.status, codeFor(response.status, response.data), Number.isFinite(retryAfter) ? retryAfter : undefined);
    }
    return response.data;
  },

  update: async (id: number, payload: UpdateDocumentPayload): Promise<DocumentResponse> => {
    const response = await axios.patch(`${API_URL}/documents/${id}`, payload);
    return response.data;
  },

  remove: async (id: number): Promise<DeleteDocumentResponse> => {
    const response = await axios.delete(`${API_URL}/documents/${id}`);
    return response.data;
  },
};
